/**
 * 月締め作業の保存(`month_close/{YYYY-MM}`)。2026-09-24 発注者指示。P-67。
 * 入力するのは **前払い・調整・キャンセル件数** と **通話料の取込結果**だけで、
 * 報酬・交通費・請求金額は日次のデータから毎回計算する(元データを二重に持たない)。
 */
import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { getDb } from '@core/lib/firebase';
import { writeAuditLog } from '@core/lib/auditLog';
import type { CallChargeByNumber, CallChargeRow } from '@core/import/callCharge';
import type { CancelBasis } from '@core/import/cancelReport';
import type { PayBasis } from './monthClose';

/** 人(BB ID)ごとの手入力 */
export type MonthPayInput = {
  /** 前払い(その月にすでに渡した分) */
  advance?: number;
  /** 調整(手当・控除。マイナスも可) */
  adjust?: number;
  note?: string;
};

export type MonthCallCharges = {
  file_name: string;
  total_rows: number;
  total_sec: number;
  total_yen: number;
  rows: CallChargeRow[];
  /** 発信番号ごとの合計(用途別の内訳。2026-09-28) */
  by_number?: CallChargeByNumber[];
  /** **PLに反映する(確定)**。人が確認してから押す(2026-09-28 発注者指示「自動で変わるのは避けたい」) */
  applied?: boolean;
  applied_at?: unknown;
  applied_by?: string;
  unknown_numbers: { number: string; calls: number; yen: number }[];
  imported_at?: unknown;
  imported_by?: string;
};

/** 顧客情報レポートCSVから読んだキャンセル(2026-09-28。対応者情報=「キャンセル」) */
export type MonthCancelAuto = {
  basis: CancelBasis;
  /** 読んだファイル名(会社ごとに1つずつ) */
  files: string[];
  by_bb_id: Record<string, number>;
  unknown: { name: string; count: number }[];
  total: number;
  businesses: string[];
  imported_at?: unknown;
  imported_by?: string;
};

export type MonthClose = {
  month: string;
  /** 支払いの基準(日ごと / 月まとめ)。どちらを正にするかは未確定(P-1) */
  basis: PayBasis;
  pay: Record<string, MonthPayInput>;
  /** 歩合のキャンセル件数(BB ID ごと) */
  cancels: Record<string, number>;
  calls?: MonthCallCharges | null;
  /** CSVから読んだキャンセル(手入力の「キャンセル(入力)」とは別に持つ) */
  cancels_auto?: MonthCancelAuto | null;
  /** 確定した日時(確定後も編集はできる。印だけ) */
  confirmed_at?: unknown;
  confirmed_by?: string;
};

export function emptyMonthClose(month: string): MonthClose {
  return { month, basis: 'daily', pay: {}, cancels: {} };
}

function normalize(month: string, data: Record<string, unknown> | undefined): MonthClose {
  const d = data ?? {};
  const pay: Record<string, MonthPayInput> = {};
  for (const [k, v] of Object.entries((d.pay as Record<string, MonthPayInput>) ?? {})) {
    pay[k] = {
      advance: Number.isFinite(Number(v?.advance)) ? Number(v?.advance) : undefined,
      adjust: Number.isFinite(Number(v?.adjust)) ? Number(v?.adjust) : undefined,
      note: typeof v?.note === 'string' ? v.note : undefined,
    };
  }
  const cancels: Record<string, number> = {};
  for (const [k, v] of Object.entries((d.cancels as Record<string, number>) ?? {})) {
    if (Number.isFinite(Number(v))) cancels[k] = Number(v);
  }
  return {
    month,
    basis: d.basis === 'month' ? 'month' : 'daily',
    pay,
    cancels,
    calls: (d.calls as MonthCallCharges | undefined) ?? null,
    cancels_auto: (d.cancels_auto as MonthCancelAuto | undefined) ?? null,
    confirmed_at: d.confirmed_at,
    confirmed_by: typeof d.confirmed_by === 'string' ? d.confirmed_by : undefined,
  };
}

export async function fetchMonthClose(month: string): Promise<MonthClose> {
  const snap = await getDoc(doc(getDb(), 'month_close', month));
  return normalize(month, snap.data());
}

/** ①②の入力(前払い・調整・キャンセル)と基準を保存する */
export async function saveMonthClose(month: string, v: Pick<MonthClose, 'basis' | 'pay' | 'cancels'>, userId: string): Promise<void> {
  await setDoc(
    doc(getDb(), 'month_close', month),
    { month, basis: v.basis, pay: v.pay, cancels: v.cancels, updated_by: userId, updated_at: serverTimestamp() },
    { merge: true },
  );
  await writeAuditLog({
    targetType: 'import',
    targetId: `month_close/${month}`,
    action: 'month-close-save',
    by: userId,
    changes: [`月締め(${month}): 支払いの基準=${v.basis === 'month' ? '月まとめ' : '日ごと'} ／ 前払い・調整 ${Object.keys(v.pay).length}件 ／ キャンセル ${Object.keys(v.cancels).length}件`],
  });
}

/** ③通話料の取込結果を保存する(明細は保存せず、会社×日の集計だけ) */
export async function saveMonthCallCharges(month: string, calls: MonthCallCharges, userId: string): Promise<void> {
  await setDoc(
    doc(getDb(), 'month_close', month),
    { month, calls: { ...calls, imported_at: serverTimestamp(), imported_by: userId }, updated_by: userId, updated_at: serverTimestamp() },
    { merge: true },
  );
  await writeAuditLog({
    targetType: 'import',
    targetId: `month_close/${month}`,
    action: 'month-close-calls',
    by: userId,
    changes: [`通話料の取込(${month}): ${calls.file_name} ／ ${calls.total_rows}件 ／ ¥${Math.round(calls.total_yen)} ／ 未登録の番号 ${calls.unknown_numbers.length}件`],
  });
}

/** ②のキャンセルをCSVから読んだ結果を保存する */
export async function saveMonthCancelAuto(month: string, auto: MonthCancelAuto, userId: string): Promise<void> {
  await setDoc(
    doc(getDb(), 'month_close', month),
    { month, cancels_auto: { ...auto, imported_at: serverTimestamp(), imported_by: userId }, updated_by: userId, updated_at: serverTimestamp() },
    { merge: true },
  );
  await writeAuditLog({
    targetType: 'import',
    targetId: `month_close/${month}`,
    action: 'month-close-cancels',
    by: userId,
    changes: [`キャンセルの取込(${month}): ${auto.files.join(' / ')} ／ ${auto.total}件 ／ 基準=${auto.basis}`],
  });
}

/** ③通話料の実績を PL に反映する／やめる(2026-09-28 発注者指示。人が確認してから切り替える) */
export async function applyMonthCallCharges(month: string, applied: boolean, userId: string): Promise<void> {
  await setDoc(
    doc(getDb(), 'month_close', month),
    { month, calls: { applied, applied_at: serverTimestamp(), applied_by: userId }, updated_by: userId, updated_at: serverTimestamp() },
    { merge: true },
  );
  await writeAuditLog({
    targetType: 'import',
    targetId: `month_close/${month}`,
    action: 'month-close-calls-apply',
    by: userId,
    changes: [`通話料の実績を PL に${applied ? '反映' : '反映しない'}(${month})`],
  });
}

/** PLで使う: その月の通話料の実績(確定したものだけ)。確定していなければ null */
export async function fetchAppliedCallCharges(month: string): Promise<MonthCallCharges | null> {
  const v = await fetchMonthClose(month);
  return v.calls && v.calls.applied ? v.calls : null;
}

/** 月を確定した印を付ける */
export async function confirmMonthClose(month: string, userId: string): Promise<void> {
  await setDoc(doc(getDb(), 'month_close', month), { month, confirmed_at: serverTimestamp(), confirmed_by: userId }, { merge: true });
  await writeAuditLog({ targetType: 'import', targetId: `month_close/${month}`, action: 'month-close-confirm', by: userId, changes: [`月締め(${month})を確定`] });
}
